import { useMemo } from "react";
import { Grid, Button, Dropdown } from "semantic-ui-react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { ErrorInfo, FieldRow } from "components/form";
import { toast } from "components/Toast/Toast";
import { validateEmail, usersToSelectOptions } from "services/common";
import { AgencyType } from "models";

export const DigitalServiceForm = ({
  digitalServiceToUpdate,
  closeDigitalServiceForm,
  users,
  dispatch,
  t,
}) => {
  const digitalServiceSchema = yup.object().shape({
    name: yup.string().required(t("required")),
    email: yup.string().required(t("required")).test("is-email", t("invalid_email"), validateEmail),
    contacts: yup.array().min(1, t("min_one_contact_error")),
  });

  const initialValues = useMemo(
    () =>
      !digitalServiceToUpdate
        ? {
            name: "",
            email: "",
            contacts: [],
          }
        : {
            name: digitalServiceToUpdate.name,
            email: digitalServiceToUpdate.email,
            contacts: digitalServiceToUpdate.contacts,
          },
    [digitalServiceToUpdate]
  );

  const submitCreate = async (values) => {
    try {
      await dispatch.agencys.create({ ...values, type: AgencyType.DIGITAL_SERVICE });
      toast(undefined, t("digital_service_create_success"));
    } catch (error) {
      console.error(error);
      toast("error", t("digital_service_create_failure"));
    }
    closeDigitalServiceForm();
  };

  const submitUpdate = async (values) => {
    try {
      await dispatch.agencys.update({ id: digitalServiceToUpdate.id, ...values });
      toast(undefined, t("digital_service_update_success"));
    } catch (error) {
      console.error(error);
      toast("error", t("digital_service_update_failure"));
    }
    closeDigitalServiceForm();
  };

  return (
    <Formik
      validationSchema={digitalServiceSchema}
      initialValues={initialValues}
      onSubmit={digitalServiceToUpdate ? submitUpdate : submitCreate}
    >
      {({ values, errors, isSubmitting, setFieldValue, submitCount }) => (
        <Form>
          <h3>
            {digitalServiceToUpdate ? t("digital_service_update") : t("digital_service_create")}:
          </h3>
          <Grid columns={2} verticalAlign="middle">
            <FieldRow label={t("name")} name="name" />
            <FieldRow label={t("emails")} name="email">
              <span className="help-text">{t("digital_service_emails_help_text")}</span>
            </FieldRow>
            <Grid.Row className="form-field-row">
              <Grid.Column width={3} textAlign="right">
                <label htmlFor="contacts">{t("contacts")}</label>
              </Grid.Column>
              <Grid.Column width={13}>
                <Dropdown
                  name="contacts"
                  placeholder={t("contacts_placeholder")}
                  value={values.contacts}
                  fluid
                  multiple
                  search
                  selection
                  options={usersToSelectOptions(users)}
                  onChange={(_e, { value, name }) => setFieldValue(name, value)}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <ErrorInfo errors={errors} submitCount={submitCount} t={t} tPrefix={""} />
          <div className="form-footer">
            <Button type="button" primary onClick={() => closeDigitalServiceForm()}>
              {t("cancel")}
            </Button>
            <Button primary type="submit" loading={isSubmitting}>
              {digitalServiceToUpdate ? t("update") : t("create")}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
